import React, { useState } from 'react';
import './index.css';
import { colors } from './utils/colors';
import type { Teacher, ActiveTab, TeacherAttendanceRecord } from './utils/types';

import SplashScreen     from './components/SplashScreen';
import LoginScreen      from './components/LoginScreen';
import HomeScreen       from './components/HomeScreen';
import ProfileScreen    from './components/ProfileScreen';
import PWAInstallBanner from './components/PWAInstallBanner';
import TeacherAttendance from './modules/teacher-attendance/TeacherAttendance';
import ClassAttendance   from './modules/class-attendance/ClassAttendance';
import { logout } from './Api/auth';

function App() {
  const [splash,  setSplash]  = useState(true);
  const [teacher, setTeacher] = useState<Teacher | null>(() => {
    try {
      const s = localStorage.getItem('vav_teacher');
      return s ? JSON.parse(s) : null;
    } catch { return null; }
  });
  const [activeTab, setActiveTab] = useState<ActiveTab>('home');

  const [myAttendance, setMyAttendance] = useState<TeacherAttendanceRecord | null>(() => {
    try {
      const s = localStorage.getItem('vav_my_attendance');
      if (!s) return null;
      const r: TeacherAttendanceRecord = JSON.parse(s);
      const today = new Date().toISOString().split('T')[0];
      return r.date === today ? r : null;
    } catch { return null; }
  });

  const handleLogin = (t: Teacher) => {
    setTeacher(t);
    localStorage.setItem('vav_teacher', JSON.stringify(t));
    setActiveTab('home');
  };

  const handleLogout = async () => {
    try { await logout(); } catch { /* ignore server errors on logout */ }
    localStorage.removeItem('vav_teacher');
    localStorage.removeItem('vav_my_attendance');
    setTeacher(null);
    setMyAttendance(null);
    setActiveTab('home');
  };

  const handleSaveMyAttendance = (rec: TeacherAttendanceRecord) => {
    setMyAttendance(rec);
    localStorage.setItem('vav_my_attendance', JSON.stringify(rec));
  };

  if (splash) return <SplashScreen onDone={() => setSplash(false)} />;
  if (!teacher) return <LoginScreen onLogin={handleLogin} />;

  const renderTab = () => {
    switch (activeTab) {
      case 'my-attendance':
        return (
          <TeacherAttendance
            teacher={teacher}
            record={myAttendance}
            onSave={handleSaveMyAttendance}
            onBack={() => setActiveTab('home')}
          />
        );
      case 'class-attendance':
        return <ClassAttendance teacher={teacher} onBack={() => setActiveTab('home')} />;
      case 'profile':
        return (
          <ProfileScreen
            teacher={teacher}
            onBack={() => setActiveTab('home')}
            onLogout={handleLogout}
          />
        );
      case 'home':
      default:
        return (
          <HomeScreen
            teacher={teacher}
            myAttendance={myAttendance}
            onNavigate={setActiveTab}
            onLogout={handleLogout}
          />
        );
    }
  };

  return (
    <div style={{
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      background: colors.bg,
      minHeight: '100dvh',
      position: 'relative',
    }}>
      {renderTab()}
      <PWAInstallBanner />
    </div>
  );
}

export default App;
